import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';

// Custom SVG Icons
const FileDownloadIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const SearchIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
  </svg>
);

const PersonIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
  </svg>
);

const PeopleIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
  </svg>
);

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Consolidated table configurations
const TABLE_CONFIGS = {
  volumeTrends: {
    title: 'Ticket Volume Trends',
    columns: [
      { id: 'date', label: 'Date', numeric: false },
      { id: 'total', label: 'Total', numeric: true },
      { id: 'resolved', label: 'Resolved', numeric: true }
    ]
  },
  priority: {
    title: 'Priority Distribution',
    columns: [
      { id: 'priority', label: 'Priority Level', numeric: false },
      { id: 'count', label: 'Count', numeric: true },
      { id: 'percentage', label: 'Percentage', numeric: true, format: (value) => `${value.toFixed(1)}%` }
    ]
  },
  category: {
    title: 'Category Performance',
    columns: [
      { id: 'category', label: 'Category', numeric: false },
      { id: 'count', label: 'Tickets', numeric: true },
      { id: 'resolutionTime', label: 'Avg. Resolution Time (h)', numeric: true, format: (value) => value.toFixed(1) }
    ]
  },
  responseTime: {
    title: 'Response Time Analysis',
    columns: [
      { id: 'date', label: 'Date', numeric: false },
      { id: 'responseTime', label: 'Response Time (h)', numeric: true, format: (value) => value.toFixed(1) },
      { id: 'resolutionTime', label: 'Resolution Time (h)', numeric: true, format: (value) => value.toFixed(1) }
    ]
  },
  ictCoor: {
    title: 'ICT COOR Distribution by School Type',
    columns: [
      { id: 'type', label: 'School Type', numeric: false },
      { id: 'count', label: 'Number of ICT COORs', numeric: true },
      { id: 'percentage', label: 'Percentage', numeric: true, format: (value) => `${value.toFixed(0)}%` }
    ]
  }
};

// Summary card configurations
const SUMMARY_CARDS = [
  {
    title: 'Total Tickets',
    getValue: (stats) => stats.totalTickets,
    getSubtext: (stats) => `Resolution Rate: ${stats.performanceMetrics.resolutionRate.toFixed(1)}%`
  },
  {
    title: 'Response Time',
    getValue: (stats) => `${stats.responseTimeMetrics.averageResponseTime}h`,
    getSubtext: (stats) => `Efficiency: ${stats.performanceMetrics.responseEfficiency.toFixed(1)}%`
  },
  {
    title: 'Resolution Time',
    getValue: (stats) => `${stats.responseTimeMetrics.averageResolutionTime}h`,
    getSubtext: () => 'Average time to resolve'
  },
  {
    title: 'Satisfaction Score',
    getValue: (stats) => `${stats.performanceMetrics.satisfactionScore.toFixed(1)}%`,
    getSubtext: () => 'Based on resolution time'
  },
  {
    title: 'ICT COORs',
    getValue: (stats) => stats.ictCoorUsers || 0,
    getSubtext: () => 'Total ICT Coordinators',
    icon: PersonIcon,
    color: '#651fff'
  }
];

const PaginatedTable = ({ config, data, searchable = true }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const filteredData = searchable 
    ? data.filter(row => 
    Object.values(row).some(value => 
      String(value).toLowerCase().includes(searchQuery.toLowerCase())
    )
      )
    : data;

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
      <div className={`mb-4 flex ${isMobile ? 'flex-col gap-2' : 'flex-row justify-between items-center'}`}>
        <h3 className="text-lg sm:text-xl font-semibold font-poppins text-blue-600">
          {config.title}
        </h3>
        {searchable && (
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins ${
                isMobile ? 'w-full' : 'w-48'
              }`}
            />
          </div>
        )}
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              {config.columns.map((column) => (
                <th 
                  key={column.id}
                  className={`py-3 px-2 font-poppins font-semibold text-sm whitespace-nowrap ${
                    column.numeric ? 'text-right' : 'text-left'
                  }`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => (
              <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                {config.columns.map((column) => (
                  <td 
                    key={column.id}
                    className={`py-3 px-2 font-poppins text-sm whitespace-nowrap ${
                      column.numeric ? 'text-right' : 'text-left'
                    }`}
                  >
                    {column.format ? column.format(row[column.id]) : row[column.id]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Custom Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 font-poppins">Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            className="px-2 py-1 border border-gray-300 rounded text-sm font-poppins"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            {!isMobile && <option value={25}>25</option>}
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 font-poppins">
            {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredData.length)} of {filteredData.length}
          </span>
          
          <div className="flex gap-1">
            <button
              onClick={() => setPage(0)}
              disabled={page === 0}
              className="px-2 py-1 border border-gray-300 rounded text-sm font-poppins disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              First
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              className="px-2 py-1 border border-gray-300 rounded text-sm font-poppins disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
              className="px-2 py-1 border border-gray-300 rounded text-sm font-poppins disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
            <button
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
              className="px-2 py-1 border border-gray-300 rounded text-sm font-poppins disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Last
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ config, stats }) => {
  const Icon = config.icon;
  
  return (
    <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
      <div className={`flex items-center ${Icon ? 'mb-2' : 'mb-0'}`}>
        {Icon && <Icon className={`w-5 h-5 mr-2 text-[${config.color}]`} />}
        <h4 className="text-sm sm:text-base font-medium font-poppins text-gray-700">
          {config.title}
        </h4>
      </div>
      <div 
        className={`text-2xl sm:text-4xl font-bold font-poppins mb-1 ${
          config.color ? `text-[${config.color}]` : 'text-gray-900'
        }`}
      >
        {config.getValue(stats)}
      </div>
      <p className="text-xs sm:text-sm text-gray-600 font-poppins">
        {config.getSubtext(stats)}
      </p>
    </div>
  );
};

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [timePeriod, setTimePeriod] = useState('monthly');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Generate ICT COOR distribution data
  const generateIctCoorDistribution = useCallback((totalIctCoors) => [
    { type: 'Elementary School', count: Math.round(totalIctCoors * 0.55), percentage: 55 },
    { type: 'Junior High School', count: Math.round(totalIctCoors * 0.25), percentage: 25 },
    { type: 'Senior High School', count: Math.round(totalIctCoors * 0.15), percentage: 15 },
    { type: 'Integrated School', count: Math.round(totalIctCoors * 0.05), percentage: 5 },
  ], []);

  // Map frontend time period values to backend expected values
  const getBackendTimeRange = (timePeriod) => {
    const timeRangeMap = {
      'today': '24hours',
      'monthly': '30days',
      'quarter': '90days'
    };
    return timeRangeMap[timePeriod] || '30days';
  };

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const backendTimeRange = getBackendTimeRange(timePeriod);
      console.log(`Fetching report data for time period: ${timePeriod} (backend: ${backendTimeRange})`);

      const response = await axios.get(`${API_BASE_URL}/admin/stats?time_range=${backendTimeRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStats(response.data.data);
        setError(null);
        console.log('Report data fetched successfully:', response.data.data);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError(err.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [timePeriod]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const exportToExcel = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const backendTimeRange = getBackendTimeRange(timePeriod);
      const response = await axios.get(`${API_BASE_URL}/admin/tickets/export?time_period=${backendTimeRange}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const date = new Date().toISOString().split('T')[0];
      saveAs(blob, `tickets_report_${timePeriod}_${date}.xlsx`);
      setError(null);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError(err.message || 'Failed to export data to Excel');
    } finally {
      setLoading(false);
    }
  };

  // Prepare data with calculations
  const preparedData = stats ? {
    priorityData: stats.priorityDistribution.map(row => ({
    ...row,
    percentage: (row.count / stats.totalTickets) * 100
    })),
    ictCoorDistribution: generateIctCoorDistribution(stats.ictCoorUsers || 0)
  } : null;

  if (loading && !stats) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[400px] p-4 sm:p-8">
        <div className={`animate-spin rounded-full border-b-2 border-blue-600 ${isMobile ? 'h-10 w-10' : 'h-12 w-12'}`}></div>
        <p className="font-poppins mt-4 text-gray-600 text-sm sm:text-base">
          Loading report data...
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-poppins text-gray-900 leading-tight mb-3">
          Advanced<br /> 
          <span className="text-blue-600 relative">
            Reports
            <span className="absolute bottom-0 left-0 w-full h-2 bg-blue-100 rounded-sm -z-10"></span>
          </span>
        </h1>
      </div>
      
      {/* Controls */}
      <div className={`flex ${isMobile ? 'flex-col gap-4' : 'flex-row justify-end items-center'} mb-6`}>
        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'flex-row gap-4'} items-center ${isMobile ? 'w-full' : ''}`}>
          {/* Time Period Toggle */}
          <div className={`flex ${isMobile ? 'w-full' : ''}`}>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
              {['today', 'monthly', 'quarter'].map((period) => (
                <button
                  key={period}
                  onClick={() => {
                    if (period !== timePeriod) {
                      console.log(`Time period changed from ${timePeriod} to ${period}`);
                      setTimePeriod(period);
                    }
                  }}
                  disabled={loading}
                  className={`px-4 py-2 text-sm font-poppins transition-colors ${
                    timePeriod === period
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } ${isMobile ? 'flex-1' : ''} disabled:opacity-50`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>
          </div>
          
          {/* Export Button */}
          <button
            onClick={exportToExcel}
            disabled={loading}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-poppins text-sm whitespace-nowrap disabled:opacity-50 ${isMobile ? 'w-full justify-center' : ''}`}
          >
            <FileDownloadIcon className="w-4 h-4" />
            Export {timePeriod.charAt(0).toUpperCase() + timePeriod.slice(1)} Report
          </button>
        </div>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800 font-poppins">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Loading indicator for data refresh */}
      {loading && stats && (
        <div className="flex justify-center items-center mb-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <p className="ml-2 text-sm text-gray-600 font-poppins">Updating data...</p>
        </div>
      )}
      
      {/* Summary Cards */}
      {stats && (
        <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6 ${loading ? 'opacity-60' : 'opacity-100'}`}>
          {SUMMARY_CARDS.map((config, index) => (
            <SummaryCard key={index} config={config} stats={stats} />
          ))}
        </div>
      )}
      
      {/* Analytics Tables */}
      {stats && preparedData && (
        <div className={`space-y-6 ${loading ? 'opacity-60' : 'opacity-100'}`}>
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold font-poppins text-gray-900 mb-2">
              Ticket Analytics
            </h2>
            <div className="border-b border-gray-200 mb-6"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PaginatedTable
              config={TABLE_CONFIGS.volumeTrends}
              data={stats.ticketTrends}
            />
            
            <PaginatedTable
              config={TABLE_CONFIGS.priority}
              data={preparedData.priorityData}
            />
            
            <PaginatedTable
              config={TABLE_CONFIGS.category}
              data={stats.categoryDistribution}
            />
            
            <PaginatedTable
              config={TABLE_CONFIGS.responseTime}
              data={stats.ticketTrends}
            />
          </div>
          
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold font-poppins text-gray-900 mb-2 flex items-center gap-2">
              <PeopleIcon className="w-6 h-6 text-purple-600" />
              ICT Coordinator Analytics
            </h2>
            <div className="border-b border-gray-200 mb-6"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PaginatedTable
              config={TABLE_CONFIGS.ictCoor}
              data={preparedData.ictCoorDistribution}
            />
            
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200 flex flex-col justify-center">
              <h3 className="text-lg sm:text-xl font-semibold font-poppins text-gray-900 mb-4">
                ICT Coordinator Overview
              </h3>
              
              <div className="mb-4">
                <h4 className="text-base sm:text-lg font-bold font-poppins text-gray-900 mb-2">
                  {stats.ictCoorUsers || 0} Total ICT Coordinators
                </h4>
                <p className="text-sm sm:text-base text-gray-600 font-poppins mb-4">
                  Responsible for ticket management at their schools
                </p>
                
                <div className="space-y-2">
                  {['View and respond to technical issues', 'Coordinate with SDO ICT Unit', 'Provide first-level technical support', 'Manage school\'s ICT resources'].map((item, index) => (
                    <p key={index} className="text-sm sm:text-base text-gray-700 font-poppins">
                      • {item}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports; 